import { ReolyzerFactory } from "../reolyzer/reolyzer-factory";
import { getCheckedColumns } from "./taskpane";

export async function generateColumns() {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();

      const usedRange = sheet.getUsedRange();
      usedRange.load("values, columnCount, rowCount");
      await context.sync();

      const headers = usedRange.values?.[0];
      if (!headers) {
        console.error("Header row is empty or could not be loaded.");
        return;
      }

      const rowCount = usedRange.rowCount;

      // ✅ use only checked headers
      const fixedHeaders = getCheckedColumns();

      const headerToColumnIndex: { [key: string]: number } = {};
      headers.forEach((header, i) => {
        headerToColumnIndex[header] = i;
      });

      const targetHeader = "cadastral_reference";
      const colIndex = headerToColumnIndex[targetHeader];
      if (colIndex === undefined) {
        console.error(`Column "${targetHeader}" not found.`);
        return;
      }

      let currentColumnCount = usedRange.columnCount;
      let currentHeaders = [...headers];

      // Insert missing fixed headers (only those the user selected)
      for (const header of fixedHeaders) {
        if (header === "reolyzer_cadaster_construction_data") continue; // skip the toggle
        if (!(header in headerToColumnIndex)) {
          const insertRange = sheet
            .getRangeByIndexes(0, currentColumnCount, rowCount, 1)
            .getEntireColumn();
          insertRange.insert(Excel.InsertShiftDirection.right);

          const headerCell = sheet.getCell(0, currentColumnCount);
          headerCell.values = [[header]];

          const sourceFormat = sheet.getRangeByIndexes(0, colIndex, rowCount, 1);
          const targetFormat = sheet.getRangeByIndexes(0, currentColumnCount, rowCount, 1);
          targetFormat.copyFrom(sourceFormat, Excel.RangeCopyType.formats);

          headerToColumnIndex[header] = currentColumnCount;
          currentHeaders.push(header);
          currentColumnCount++;
        }
      }

      // Load cadastral references
      const refRange = sheet.getRangeByIndexes(1, colIndex, rowCount - 1, 1);
      refRange.load("values");
      await context.sync();

      const refs = refRange.values.map(row => row[0]);

      for (let i = 0; i < refs.length; i++) {
        const ref = refs[i];
        const lb = await ReolyzerFactory.createLinkBuilder(ref);

        // Build a mapping: header -> value
        const headerValueMap: Record<string, any> = {
          reolyzer_google_maps_link: lb.getGoogleStreetViewLink(),
          reolyzer_cadaster_info_link: lb.getCadasterInfoLink(),
          reolyzer_idealista_link: lb.getIdealistaLink(),
          reolyzer_coord_lat: lb.getLat(),
          reolyzer_coord_lng: lb.getLng(),
          reolyzer_register_province: lb.Province,
          reolyzer_register_municipality: lb.Municipality,
          reolyzer_register_address: lb.FullAddress,
        };

        // Write only selected fixed values
        for (const header of fixedHeaders) {
          if (header === "reolyzer_cadaster_construction_data") continue; // not a real column
          const col = headerToColumnIndex[header];
          const cell = sheet.getCell(i + 1, col);
          cell.values = [[headerValueMap[header] ?? ""]];
        }

        // Handle construction elements only if requested
        if (fixedHeaders.includes("reolyzer_cadaster_construction_data")) {
          const lconsElements: { lcd: string; stl: number }[] = lb.ConstructionElements ?? [];

          for (const element of lconsElements) {
            const lcdHeader = `reolyzer_register_${element.lcd.toLowerCase().replace(/ /g, "_")}_sqm`;

            // Insert missing dynamic columns
            if (!(lcdHeader in headerToColumnIndex)) {
              const insertRange = sheet
                .getRangeByIndexes(0, currentColumnCount, rowCount, 1)
                .getEntireColumn();
              insertRange.insert(Excel.InsertShiftDirection.right);

              const headerCell = sheet.getCell(0, currentColumnCount);
              headerCell.values = [[lcdHeader]];

              const sourceFormat = sheet.getRangeByIndexes(0, colIndex, rowCount, 1);
              const targetFormat = sheet.getRangeByIndexes(0, currentColumnCount, rowCount, 1);
              targetFormat.copyFrom(sourceFormat, Excel.RangeCopyType.formats);

              headerToColumnIndex[lcdHeader] = currentColumnCount;
              currentHeaders.push(lcdHeader);
              currentColumnCount++;
            }

            // Write value
            const col = headerToColumnIndex[lcdHeader];
            const cell = sheet.getCell(i + 1, col);
            cell.values = [[element.stl.toString()]];
          }
        }

        await context.sync();
        await sleep(200);
      }

      console.log("All rows processed.");
    });
  } catch (error) {
    console.error("Error running add-in:", error);
  }
}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
